import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, Circle } from 'lucide-react';
import { tabletopExercisesAPI, TabletopExercise } from '../../services/tabletopService';

interface Props {
  projectId: string;
  exercise: TabletopExercise;
  onAdvance: (exercise: TabletopExercise) => void;
}

const STAGES: { key: keyof NonNullable<TabletopExercise['generationState']>; label: string }[] = [
  { key: 'threatModel', label: 'Threat & Adversary Modelling' },
  { key: 'personas', label: 'Persona Casting' },
  { key: 'scenario', label: 'Scenario Architect' },
  { key: 'compliance', label: 'Compliance Mapping' },
];

const GenerationProgressPage: React.FC<Props> = ({ projectId, exercise, onAdvance }) => {
  const [current, setCurrent] = useState(exercise);

  useEffect(() => {
    if (current.status !== 'generating') return;
    const interval = setInterval(() => {
      tabletopExercisesAPI.get(projectId, current.exerciseId).then(r => {
        setCurrent(r.data.exercise);
        if (r.data.exercise.status !== 'generating') {
          clearInterval(interval);
          onAdvance(r.data.exercise);
        }
      }).catch(() => { /* transient poll failure, retry on next tick */ });
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.status, current.exerciseId, projectId]);

  const state = current.generationState;
  const broadcastLabel = state && state.broadcastAssetsTotal > 0
    ? `Broadcast assets (${state.broadcastAssetsDone}/${state.broadcastAssetsTotal})`
    : null;
  const broadcastDone = !!state && state.broadcastAssetsTotal > 0 && state.broadcastAssetsDone >= state.broadcastAssetsTotal;

  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="w-full max-w-md bg-white border border-[#e2e8f0] rounded-2xl p-8 flex flex-col gap-6">
        <div className="text-center">
          <Loader2 size={28} className="animate-spin text-primary mx-auto mb-3" />
          <h1 className="text-lg font-bold text-navy">Building your scenario</h1>
          <p className="text-sm text-slate-500 mt-1">This runs the five build-pipeline agents. Usually a minute or two.</p>
        </div>

        <div className="flex flex-col gap-3">
          {STAGES.map(stage => {
            const done = state?.[stage.key] === 'done';
            return (
              <div key={stage.key} className="flex items-center gap-3">
                {done ? <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" /> : <Circle size={18} className="text-slate-300 flex-shrink-0" />}
                <span className={`text-sm ${done ? 'text-navy font-medium' : 'text-slate-400'}`}>{stage.label}</span>
              </div>
            );
          })}
          {broadcastLabel && (
            <div className="flex items-center gap-3">
              {broadcastDone ? <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" /> : <Circle size={18} className="text-slate-300 flex-shrink-0" />}
              <span className={`text-sm ${broadcastDone ? 'text-navy font-medium' : 'text-slate-400'}`}>{broadcastLabel}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerationProgressPage;
